import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

// After a deploy the hashed chunk names change. A tab opened before it (or an index.html served
// from the PWA cache) still asks for the old file names, the import fails, and the page used to
// die with «Failed to fetch dynamically imported module». Reloading once fetches the fresh
// index.html with the new names — that is all the user would have to do by hand.

const RELOAD_KEY = 'lupa:chunk-reload-at'
// A second failure this soon after our own reload means the file is really missing, not stale:
// stop reloading and let the ErrorBoundary show its message instead of looping forever.
const RELOAD_COOLDOWN_MS = 30_000

// Chrome, Firefox, Safari and Vite's CSS preloader each word it differently.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return CHUNK_ERROR_RE.test(message)
}

/** Reloads the page unless we already did so moments ago. Returns whether a reload was started. */
export function reloadForNewVersion(): boolean {
  let last = 0
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
  } catch {
    // Storage blocked (private mode): without the guard, reloading could loop — so don't.
    return false
  }
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadForNewVersion()) {
        // The page is going away; never settle so React keeps showing the Suspense fallback.
        return new Promise<{ default: T }>(() => {})
      }
      throw error
    }),
  )
}
